import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

const expertises = [
  {
    id: 'affaires',
    label: 'Droit des affaires',
    icon: 'ri-briefcase-4-line',
    description: 'Conseil et contentieux pour les entreprises, de la création à la restructuration.',
    items: [
      'Contentieux entre associés',
      'Corporate',
      'Recouvrement de créance',
      'Convention d\'abonnement',
      'Contentieux commercial divers',
      'Droit des entreprises en difficulté',
      'Droit social',
      'Fusion / absorption',
    ],
    image: 'https://public.readdy.ai/ai/img_res/70ad5ea5adbb9f8441fc66d9af536723.jpg',
  },
  {
    id: 'penal',
    label: 'Droit pénal',
    icon: 'ri-shield-keyhole-line',
    description: 'Défense pénale des personnes physiques et morales devant toutes les juridictions.',
    items: [
      'Droit Pénal général',
      'Défense pénale',
      'Droit pénal des affaires',
      'Droit des victimes',
      'Droit pénal militaire',
      'Tribunal correctionnel',
      'Cours d\'assises',
    ],
    image: 'https://readdy.ai/api/search-image?query=Courtroom%20interior%20with%20wooden%20benches%2C%20judge%20podium%2C%20scales%20of%20justice%2C%20dramatic%20lighting%20through%20tall%20windows%2C%20classical%20architecture%2C%20serious%20and%20authoritative%20atmosphere%2C%20muted%20tones&width=800&height=600&seq=expertise-penal-001&orientation=landscape',
  },
  {
    id: 'contrats',
    label: 'Droit des contrats',
    icon: 'ri-file-text-line',
    description: 'Rédaction, négociation et contentieux contractuels pour sécuriser vos engagements.',
    items: [
      'Contrats commerciaux',
      'Contrats entre associés',
      'Contrats civils',
      'Contrats divers',
    ],
    image: 'https://readdy.ai/api/search-image?query=Legal%20contract%20documents%20on%20elegant%20desk%20with%20fountain%20pen%2C%20notary%20office%2C%20professional%20legal%20paperwork%2C%20warm%20ambient%20lighting%2C%20classic%20wooden%20furniture%2C%20sophisticated%20law%20office%20setting&width=800&height=600&seq=expertise-contrats-001&orientation=landscape',
  },
  {
    id: 'tourisme',
    label: 'Droit du Tourisme',
    icon: 'ri-plane-line',
    description: 'Conseil et contentieux pour les professionnels du tourisme, hôteliers, agences de voyages et voyageurs dans leurs litiges et démarches contractuelles.',
    items: [
      'Droit des contrats touristiques',
      'Contentieux du tourisme',
    ],
    image: 'https://readdy.ai/api/search-image?query=Elegant%20travel%20law%20concept%20with%20luxury%20hotel%20lobby%20interior%2C%20sophisticated%20reception%20desk%20with%20marble%20floors%2C%20warm%20golden%20lighting%2C%20professional%20legal%20documents%20and%20travel%20contracts%20on%20a%20polished%20desk%2C%20minimalist%20high-end%20atmosphere%20with%20subtle%20travel%20motifs%2C%20refined%20and%20authoritative%20setting&width=800&height=600&seq=expertise-tourisme-001&orientation=landscape',
  },
  {
    id: 'sport',
    label: 'Droit du sport',
    icon: 'ri-trophy-line',
    description: 'Accompagnement juridique des sportifs, clubs et agents dans toutes leurs démarches contractuelles et contentieuses.',
    items: [
      'Accompagnement des sportifs',
      'Contrats sportifs',
      'Représentation devant les tribunaux',
      'Agent sportif',
      'Contentieux avec les fédérations sportives',
    ],
    image: 'https://readdy.ai/api/search-image?query=Professional%20sports%20law%20concept%20with%20elegant%20office%20setting%2C%20sports%20contract%20documents%20on%20a%20clean%20desk%2C%20trophy%20and%20legal%20books%20in%20background%2C%20sophisticated%20minimalist%20interior%20with%20warm%20golden%20lighting%2C%20dark%20wood%20accents%2C%20premium%20law%20firm%20atmosphere%20representing%20sports%20and%20justice&width=800&height=600&seq=expertise-sport-001&orientation=landscape',
  },
];

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('affaires');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'LegalService',
        name: 'Cabinet Joël Gautier',
        description: 'Avocat au Barreau de Paris depuis 2007, spécialisé en droit des affaires, droit pénal, droit des contrats et droit du sport.',
        url: siteUrl,
        telephone: '+33183627658',
        email: 'joelgautier.avocat@yahoo.fr',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '91 boulevard Bessières',
          addressLocality: 'Paris',
          postalCode: '75017',
          addressCountry: 'FR',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 48.8955,
          longitude: 2.3178,
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '19:00',
          },
        ],
        priceRange: '€€',
        areaServed: { '@type': 'City', name: 'Paris' },
        founder: {
          '@type': 'Person',
          name: 'Joël Gautier',
          jobTitle: 'Avocat au Barreau de Paris',
        },
        knowsAbout: ['Droit des affaires', 'Droit pénal', 'Droit des contrats', 'Droit du Tourisme', 'Droit du sport'],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Cabinet Joël Gautier',
        url: siteUrl,
        inLanguage: 'fr-FR',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteUrl}/?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
    ];
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-home';
    script.textContent = JSON.stringify(schemas);
    const existing = document.getElementById('schema-home');
    if (existing) existing.remove();
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-home');
      if (el) el.remove();
    };
  }, []);

  const activeExpertise = expertises.find(e => e.id === activeTab)!;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="https://static.readdy.ai/image/2457a24757fcc37e63ebfd614e72adca/b1558608be687494406c07fd3a70dac7.jpeg" alt="Maître Joël Gautier" className="h-28 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={`text-sm font-medium transition-colors ${scrolled ? 'text-gray-900 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Accueil</Link>
            <Link to="/tarifs" className={`text-sm font-medium transition-colors ${scrolled ? 'text-gray-900 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Tarifs</Link>
            <Link to="/a-propos" className={`text-sm font-medium transition-colors ${scrolled ? 'text-gray-900 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>À propos</Link>
            <Link to="/actualites" className={`text-sm font-medium transition-colors ${scrolled ? 'text-gray-900 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Actualités</Link>
            <a href="#contact" className={`text-sm font-medium transition-colors ${scrolled ? 'text-gray-900 hover:text-[#C9A961]' : 'text-white hover:text-[#C9A961]'}`}>Contact</a>
          </div>
          <a href="#contact" className="hidden md:block px-6 py-2.5 border border-[#C9A961] text-sm font-medium rounded-full transition-all hover:bg-[#C9A961] hover:text-white whitespace-nowrap cursor-pointer" style={{ color: scrolled ? '#1A2B4A' : 'white' }}>
            
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://readdy.ai/api/search-image?query=Modern%20elegant%20law%20library%20with%20leather%20bound%20books%20on%20wooden%20shelves%2C%20professional%20legal%20office%20interior%20with%20warm%20ambient%20lighting%2C%20sophisticated%20atmosphere%2C%20architectural%20photography%20style%20with%20rich%20textures%20and%20depth%2C%20slightly%20desaturated%20tones%20for%20text%20overlay%20readability&width=1920&height=1080&seq=hero-law-library-001&orientation=landscape" 
            alt="Bibliothèque juridique" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">

          <p className="text-base font-semibold tracking-widest text-[#C9A961] uppercase mb-4" style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.25em' }}>
            Cabinet Gautier
          </p>
          <h1 className="text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Avocat au Barreau<br />de Paris
          </h1>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto font-light">
            Rigueur juridique, écoute et réactivité — spécialisé en droit des affaires et droit pénal depuis 2007
          </p>
          <a href="#contact" className="inline-flex items-center gap-4 px-8 py-4 bg-white text-gray-900 rounded-full font-medium shadow-lg hover:shadow-xl transition-all hover:scale-105 cursor-pointer whitespace-nowrap">
            Prendre Rendez-vous
            <span className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
              <i className="ri-calendar-line text-white text-sm"></i>
            </span>
          </a>
        </div>
      </section>

      {/* À Propos Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <span className="text-xs font-semibold tracking-widest text-[#C9A961] uppercase">Notre Cabinet</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-6xl font-bold text-[#1A2B4A] leading-tight mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                Votre Défenseur<br />
                Votre Conseiller<br />
                Votre Partenaire Juridique
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed max-w-xl">
                Depuis 20O7, Maître Joël Gautier accompagne particuliers et entreprises dans leurs démarches juridiques les plus complexes. Notre approche personnalisée garantit une défense sur mesure, adaptée à chaque situation unique.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-8">
              <div className="col-span-1">
                <img 
                  src="https://static.readdy.ai/image/2457a24757fcc37e63ebfd614e72adca/b3ded05d71d0716d61d22569f98c463e.jpeg" 
                  alt="Maître Joël Gautier" 
                  className="w-full h-full object-cover rounded-3xl shadow-lg"
                />
              </div>
              <div className="col-span-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20elegant%20law%20office%20meeting%20room%20with%20large%20conference%20table%2C%20contemporary%20interior%20design%2C%20natural%20light%20through%20windows%2C%20professional%20business%20environment%2C%20clean%20minimalist%20aesthetic%20with%20wooden%20accents%20and%20leather%20chairs&width=800&height=600&seq=law-office-room-001&orientation=landscape" 
                  alt="Cabinet d'avocat" 
                  className="w-full h-full object-cover rounded-3xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Domaines d'expertise Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-4">
            <span className="text-xs font-semibold tracking-widest text-[#C9A961] uppercase">Nos Compétences</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <h2 className="text-5xl font-bold text-[#1A2B4A] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Domaines<br />d'Expertise
            </h2>
            <p className="text-gray-500 text-base max-w-md leading-relaxed">
              Le Cabinet Joël Gautier intervient dans des domaines juridiques variés avec rigueur et engagement pour défendre vos intérêts.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-3 mb-10">
            {expertises.map(exp => (
              <button
                key={exp.id}
                onClick={() => setActiveTab(exp.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                  activeTab === exp.id
                    ? 'bg-[#1A2B4A] text-white border-[#1A2B4A] shadow-md'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-[#C9A961] hover:text-[#C9A961]'
                }`}
              >
                <span className={`w-5 h-5 flex items-center justify-center`}>
                  <i className={`${exp.icon} text-base`}></i>
                </span>
                {exp.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
            {/* Left: image */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg" style={{ minHeight: '420px' }}>
              <img
                key={activeExpertise.id}
                src={activeExpertise.image}
                alt={activeExpertise.label}
                className="w-full h-full object-cover object-top absolute inset-0 transition-opacity duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A2B4A]/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A961] text-white text-sm font-semibold rounded-full">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${activeExpertise.icon}`}></i>
                  </span>
                  {activeExpertise.label}
                </span>
              </div>
            </div>

            {/* Right: details */}
            <div className="flex flex-col justify-center bg-gray-50 rounded-2xl p-10">
              <p className="text-gray-500 text-base mb-8 leading-relaxed">{activeExpertise.description}</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {activeExpertise.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                    <span className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-checkbox-circle-fill text-[#C9A961] text-base"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-3 px-6 py-3 bg-[#1A2B4A] text-white rounded-full text-sm font-medium hover:bg-[#C9A961] transition-all cursor-pointer whitespace-nowrap"
                >
                  Consulter pour ce domaine
                  <span className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Honoraires Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-4">
            <span className="text-xs font-semibold tracking-widest text-[#C9A961] uppercase">Honoraires</span>
          </div>
          <h2 className="text-5xl font-bold text-[#1A2B4A] mb-16 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Nos Honoraires
          </h2>

          {/* Citation */}
          <div className="relative bg-[#1A2B4A] rounded-3xl p-12 mb-16 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-5">
              <i className="ri-double-quotes-l text-[#C9A961] text-[300px] absolute -top-10 -left-10"></i>
            </div>
            <div className="relative z-10 max-w-3xl mx-auto text-center">
              <i className="ri-double-quotes-l text-[#C9A961] text-5xl mb-6 block"></i>
              <p className="text-2xl text-white font-light leading-relaxed italic mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                La question n'est pas de savoir combien vous coûte votre défense, mais combien il vous en coûtera de ne pas vous être défendu
              </p>
              <span className="text-[#C9A961] font-semibold tracking-widest text-sm uppercase">— Démosthène</span>
            </div>
          </div>

          {/* Deux types d'honoraires */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Honoraire au temps passé */}
            <div className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-xl transition-all duration-300 p-10">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A961]/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="w-16 h-16 bg-[#C9A961]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-time-line text-[#C9A961] text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Honoraire au temps passé
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Les honoraires sont calculés en fonction du temps effectivement consacré à votre dossier. Cette formule garantit une facturation juste et transparente, adaptée à la complexité réelle de votre affaire.
              </p>
              <div className="mt-8 pt-6 border-t border-gray-100">
                <a href="#contact" className="inline-flex items-center gap-2 text-sm font-medium text-[#1A2B4A] hover:text-[#C9A961] transition-colors cursor-pointer">
                  Demander un devis
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
              </div>
            </div>

            {/* Honoraire au forfait */}
            <div className="group relative overflow-hidden rounded-2xl border border-[#C9A961]/30 bg-white shadow-sm hover:shadow-xl transition-all duration-300 p-10">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A961]/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="w-16 h-16 bg-[#C9A961]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-price-tag-3-line text-[#C9A961] text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Honoraire au forfait
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Un montant global est défini en amont pour l'ensemble de la prestation. Cette formule vous offre une visibilité totale sur le coût de votre défense et vous permet de budgéter sereinement votre accompagnement juridique.
              </p>
              <div className="mt-8 pt-6 border-t border-gray-100">
                <a href="#contact" className="inline-flex items-center gap-2 text-sm font-medium text-[#1A2B4A] hover:text-[#C9A961] transition-colors cursor-pointer">
                  Demander un devis
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <a href="#contact" className="inline-flex items-center gap-4 px-8 py-4 bg-[#1A2B4A] text-white rounded-full font-medium shadow-lg hover:shadow-xl transition-all hover:scale-105 cursor-pointer whitespace-nowrap">
              Consulter nos tarifs détaillés
              <span className="w-8 h-8 bg-[#C9A961] rounded-full flex items-center justify-center">
                <i className="ri-arrow-right-line text-white text-sm"></i>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-[600px] flex items-center">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://readdy.ai/api/search-image?query=Majestic%20courthouse%20building%20at%20golden%20hour%20sunset%20with%20dramatic%20sky%2C%20neoclassical%20architecture%20with%20columns%2C%20professional%20legal%20photography%2C%20cinematic%20lighting%20with%20warm%20orange%20and%20deep%20blue%20tones%20creating%20strong%20contrast%20for%20white%20text%20overlay&width=1920&height=600&seq=courthouse-sunset-001&orientation=landscape" 
            alt="Palais de justice" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#1A2B4A]/50"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="flex justify-between items-end">
            <div>
              <h2 className="text-7xl font-bold text-white mb-12" style={{ fontFamily: 'Playfair Display, serif', textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
                prêt à défendre<br />
                vos droits ?
              </h2>
              <a href="#contact" className="inline-flex items-center gap-4 px-8 py-4 bg-white text-gray-900 rounded-full font-medium shadow-lg hover:shadow-xl transition-all hover:scale-105 cursor-pointer whitespace-nowrap">
                Contactez-nous
                <span className="w-10 h-10 bg-black rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white"></i>
                </span>
              </a>
            </div>
            <div className="text-right">
              <p className="text-2xl text-white font-light leading-relaxed" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
                
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1A2B4A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Contactez-nous</h2>
            <p className="text-lg text-gray-600">Nous sommes à votre écoute pour répondre à vos questions</p>
          </div>

          {/* Coordonnées */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#C9A961]/10 rounded-xl shrink-0">
                <i className="ri-map-pin-line text-[#C9A961] text-xl"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A2B4A] mb-1">Adresse</p>
                <p className="text-gray-600 text-sm leading-relaxed">91 boulevard Bessières<br />75017 Paris</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#C9A961]/10 rounded-xl shrink-0">
                <i className="ri-phone-line text-[#C9A961] text-xl"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A2B4A] mb-1">Téléphone</p>
                <a href="tel:+33183627658" className="text-gray-600 text-sm hover:text-[#C9A961] transition-colors">01 83 62 76 58</a>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#C9A961]/10 rounded-xl shrink-0">
                <i className="ri-mail-line text-[#C9A961] text-xl"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A2B4A] mb-1">Email</p>
                <a href="mailto:joelgautier.avocat@yahoo.fr" className="text-gray-600 text-sm hover:text-[#C9A961] transition-colors break-all">joelgautier.avocat@yahoo.fr</a>
              </div>
            </div>
          </div>

          {/* Google Maps */}
          <div className="w-full h-80 rounded-2xl overflow-hidden shadow-md mb-12">
            <iframe
              title="Cabinet Joël Gautier - 91 boulevard Bessières Paris"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2623.0!2d2.3178!3d48.8955!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66f8b8b8b8b8b%3A0x0!2s91+Boulevard+Bessi%C3%A8res%2C+75017+Paris!5e0!3m2!1sfr!2sfr!4v1700000000000!5m2!1sfr!2sfr&q=91+Boulevard+Bessi%C3%A8res,+75017+Paris,+France"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>

          <form id="contact-form" data-readdy-form className="space-y-6" action="https://readdy.ai/api/form/d6fhn51ghdq4qda6vnsg" method="POST" onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const formData = new FormData(form);
            const textarea = formData.get('message') as string;
            if (textarea && textarea.length > 500) {
              alert('Le message ne peut pas dépasser 500 caractères');
              return;
            }
            fetch(form.action, {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body: new URLSearchParams(formData as any).toString()
            }).then(() => {
              alert('Message envoyé avec succès !');
              form.reset();
            }).catch(() => {
              alert('Erreur lors de l\'envoi du message');
            });
          }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">Nom complet</label>
                <input type="text" id="nom" name="nom" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent outline-none transition-all" />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input type="email" id="email" name="email" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent outline-none transition-all" />
              </div>
            </div>
            <div>
              <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
              <input type="tel" id="telephone" name="telephone" required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent outline-none transition-all" />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message (500 caractères max)</label>
              <textarea id="message" name="message" rows={6} maxLength={500} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent outline-none transition-all resize-none"></textarea>
            </div>
            <button type="submit" className="w-full px-8 py-4 bg-[#1A2B4A] text-white rounded-lg font-medium hover:bg-[#C9A961] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
              Envoyer le message
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1B2E] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <i className="ri-scales-line text-[#C9A961] text-[400px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></i>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-4">
              <img src="https://static.readdy.ai/image/2457a24757fcc37e63ebfd614e72adca/b1558608be687494406c07fd3a70dac7.jpeg" alt="Maître Joël Gautier" className="h-20 mb-6" />
              <h3 className="text-4xl font-light mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Justice, Ecoute<br />
                Sérieux
              </h3>
              <div className="space-y-3 mt-4">
                <div className="flex items-start gap-3 text-white/70 text-sm">
                  <i className="ri-map-pin-line text-[#C9A961] mt-0.5 shrink-0"></i>
                  <span>91 boulevard Bessières<br />75017 Paris</span>
                </div>
                <div className="flex items-center gap-3 text-white/70 text-sm">
                  <i className="ri-phone-line text-[#C9A961] shrink-0"></i>
                  <a href="tel:+33183627658" className="hover:text-[#C9A961] transition-colors">01 83 62 76 58</a>
                </div>
              </div>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
              <div>
                <h4 className="text-base font-bold mb-4">Nos Services</h4>
                <ul className="space-y-2">
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Droit des Affaires</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Droit des contrats</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Droit Pénal</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Droit du Tourisme</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Droit du Sport</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-base font-bold mb-4">Le Cabinet</h4>
                <ul className="space-y-2">
                  <li><Link to="/a-propos" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">À Propos</Link></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Équipe</a></li>
                  <li><Link to="/actualites" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Actualités</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-base font-bold mb-4">Ressources</h4>
                <ul className="space-y-2">
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">FAQ</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Guides Juridiques</a></li>
                  <li><a href="#" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Blog</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-base font-bold mb-4">Contact</h4>
                <ul className="space-y-2">
                  <li><a href="#contact" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Prendre RDV</a></li>
                  <li><a href="#contact" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Nous Écrire</a></li>
                  <li><a href="#contact" className="text-sm text-white/70 hover:text-[#C9A961] transition-colors cursor-pointer">Urgences</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="border-t border-[#C9A961]/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <p className="text-sm text-white/70">© 2025 Cabinet Joël Gautier. Tous droits réservés.</p>
              <div className="flex gap-5">
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all cursor-pointer">
                  <i className="ri-twitter-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all cursor-pointer">
                  <i className="ri-instagram-line text-lg"></i>
                </a>
              </div>
              <div className="flex gap-4 text-sm text-white/70">
                <a href="#" className="hover:text-[#C9A961] transition-colors cursor-pointer">Mentions Légales</a>
                <span>|</span>
                <a href="#" className="hover:text-[#C9A961] transition-colors cursor-pointer">Confidentialité</a>
                <span>|</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9A961] transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}